import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { planService } from 'services/planService';
import {
  ActivatePlanInput,
  CreateExerciseDto,
  CreatePlanDto,
  CreatePlanWithScheduleDto,
  CreateTreatmentPlanDto,
  Exercise,
  PlanWithScheduleResult,
  ReorderExercisesDto,
  TreatmentPlan,
  UpdateExerciseDto,
  UpdatePlanDto,
} from 'types/models';

const KEY = 'plans';

interface PlansAllParams {
  estado?: string;
  search?: string;
}

interface PaginatedPlans {
  data: TreatmentPlan[];
  meta: { total: number; page: number; limit: number; pages: number };
}

export function usePlansAll(params?: PlansAllParams) {
  return useQuery<PaginatedPlans, Error, TreatmentPlan[]>({
    queryKey: [KEY, 'all', params],
    queryFn: () => planService.listAll(params),
    select: (res) => res.data,
  });
}

export function usePlansByEpisode(patientId: string, episodeId: string) {
  return useQuery<PaginatedPlans, Error, TreatmentPlan[]>({
    queryKey: [KEY, 'episode', episodeId],
    queryFn: () => planService.listByEpisode(patientId, episodeId),
    enabled: !!patientId && !!episodeId,
    select: (res) => res.data,
  });
}

export function usePlanDetail(patientId: string, episodeId: string, planId: string | null) {
  return useQuery({
    queryKey: [KEY, 'detail', planId],
    queryFn: () => planService.getById(patientId, episodeId, planId!),
    enabled: !!patientId && !!episodeId && !!planId,
  });
}

export function useCreatePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      payload,
    }: {
      patientId: string;
      episodeId: string;
      payload: CreatePlanDto;
    }) => planService.create(patientId, episodeId, payload),
    onSuccess: (plan: TreatmentPlan) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'episode', plan.episodeId] });
    },
  });
}

export function useUpdatePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      planId,
      payload,
    }: {
      patientId: string;
      episodeId: string;
      planId: string;
      payload: UpdatePlanDto;
    }) => planService.update(patientId, episodeId, planId, payload),
    onSuccess: (plan: TreatmentPlan) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'episode', plan.episodeId] });
      queryClient.invalidateQueries({ queryKey: [KEY, 'detail', plan.id] });
    },
  });
}

export function useCreateExercise() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      planId,
      payload,
    }: {
      patientId: string;
      episodeId: string;
      planId: string;
      payload: CreateExerciseDto;
    }) => planService.createExercise(patientId, episodeId, planId, payload),
    onSuccess: (exercise: Exercise) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'detail', exercise.planId] });
    },
  });
}

export function useUpdateExercise() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      planId,
      exId,
      payload,
    }: {
      patientId: string;
      episodeId: string;
      planId: string;
      exId: string;
      payload: UpdateExerciseDto;
    }) => planService.updateExercise(patientId, episodeId, planId, exId, payload),
    onSuccess: (exercise: Exercise) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'detail', exercise.planId] });
    },
  });
}

export function useDeleteExercise() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      planId,
      exId,
    }: {
      patientId: string;
      episodeId: string;
      planId: string;
      exId: string;
    }) => planService.deleteExercise(patientId, episodeId, planId, exId),
    onSuccess: (_data: void, vars: { planId: string; patientId: string; episodeId: string; exId: string }) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'detail', vars.planId] });
    },
  });
}

export function useCreateTreatmentPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTreatmentPlanDto) => planService.createTreatmentPlan(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['episodes'] });
      queryClient.invalidateQueries({ queryKey: [KEY, 'episode', data.clinicalEpisodeId] });
    },
  });
}

export function usePreviewSchedule() {
  return useMutation({
    mutationFn: ({ planId, payload }: { planId: string; payload: ActivatePlanInput }) =>
      planService.previewSchedule(planId, payload),
  });
}

export function useActivatePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, payload }: { planId: string; payload: ActivatePlanInput }) =>
      planService.activatePlan(planId, payload),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['plans', 'detail', vars.planId] });
    },
  });
}

export function useCreatePlanWithSchedule() {
  const queryClient = useQueryClient();
  return useMutation<
    PlanWithScheduleResult,
    Error,
    { patientId: string; episodeId: string; dto: CreatePlanWithScheduleDto }
  >({
    mutationFn: ({ patientId, episodeId, dto }) =>
      planService.createWithSchedule(patientId, episodeId, dto),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'episode', vars.episodeId] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

export function useReorderExercises() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      episodeId,
      planId,
      payload,
    }: {
      patientId: string;
      episodeId: string;
      planId: string;
      payload: ReorderExercisesDto;
    }) => planService.reorderExercises(patientId, episodeId, planId, payload),
    onSuccess: (_data: Exercise[], vars: { planId: string; patientId: string; episodeId: string; payload: ReorderExercisesDto }) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'detail', vars.planId] });
    },
  });
}
